import { useState, useEffect, useRef } from 'react';
import { Search, ShoppingCart, Plus, Minus, X, Loader2, ImageIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/api/apiClient';
import { useRepProfile } from '@/lib/RepProfileContext';
import { cur } from '@/lib/currency';

function StockBadge({ qty }) {
  const n = Number(qty || 0);
  const v = n.toLocaleString('ar-EG', { maximumFractionDigits: 2 });
  if (n > 10) return <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">متوفر: {v}</span>;
  if (n > 0) return <span className="text-xs font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-md">محدود: {v}</span>;
  return <span className="text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded-md">{n < 0 ? `سالب: ${v}` : 'نفد'}</span>;
}

export function ProductCard({ product, inCart, price, onTap }) {
  return (
    <div
      onClick={() => onTap(product)}
      className={`bg-card border rounded-2xl overflow-hidden cursor-pointer transition-all active:scale-[0.98] ${
        inCart ? 'border-primary shadow-md shadow-primary/10' : 'border-border hover:border-primary/30'
      }`}
    >
      {/* Image area — fixed compact height so cards stay small */}
      <div className="w-full h-24 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center relative">
        {product._imgSrc ? (
          <img src={product._imgSrc} alt={product.name} className="w-full h-full object-contain" />
        ) : (
          <ImageIcon className="w-6 h-6 text-muted-foreground/40" strokeWidth={1.5} />
        )}
        {inCart && (
          <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center shadow">
            <span className="text-white text-xs font-bold">{inCart.quantity}</span>
          </div>
        )}
      </div>
      <div className="p-2.5 space-y-1">
        {product.default_code && <p className="text-xs text-muted-foreground">[{product.default_code}]</p>}
        <p className="text-xs font-semibold text-foreground line-clamp-2 leading-snug">{product.name}</p>
        <div className="flex items-center justify-between gap-1">
          <span className="text-primary font-bold text-sm">{Number(price || product.list_price || 0).toLocaleString('ar-EG')} {cur()}</span>
          <StockBadge qty={product.qty_available || 0} />
        </div>
      </div>
    </div>
  );
}

// Bottom sheet for adding/editing a product quantity
export function AddProductSheet({ product, price, existing, onAdd, onClose, allowEditPrice = true }) {
  const [qty, setQty] = useState(existing?.quantity || 1);
  const [unitPrice, setUnitPrice] = useState(price || product.list_price || 0);
  const [discount, setDiscount] = useState(existing?.discount || 0);

  const subtotal = qty * unitPrice * (1 - discount / 100);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-end"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="bg-card w-full rounded-t-3xl p-5 space-y-4 max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-foreground text-lg">إضافة للطلب</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-muted-foreground" /></button>
        </div>

        {/* Product info */}
        <div className="flex items-center gap-3 bg-muted/40 rounded-xl p-3">
          <div className="w-14 h-14 bg-muted rounded-xl flex items-center justify-center flex-shrink-0">
            {product._imgSrc
              ? <img src={product._imgSrc} alt={product.name} className="w-full h-full object-contain rounded-xl" />
              : <ImageIcon className="w-6 h-6 text-muted-foreground/40" strokeWidth={1.5} />
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-foreground text-sm line-clamp-2">{product.name}</p>
            {product.default_code && <p className="text-xs text-muted-foreground">[{product.default_code}]</p>}
            <p className="text-xs text-muted-foreground mt-0.5">
              {product.uom_id ? (Array.isArray(product.uom_id) ? product.uom_id[1] : product.uom_id) : 'وحدة'} •
              مخزون: {product.qty_available || 0}
            </p>
          </div>
        </div>

        {/* Quantity */}
        <div>
          <p className="text-sm text-muted-foreground font-medium mb-2">الكمية</p>
          <div className="flex items-center gap-3">
            <button onClick={() => setQty(q => Math.max(1, q - 1))}
              className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center hover:bg-muted/70 active:scale-95 transition-all">
              <Minus className="w-5 h-5" />
            </button>
            <input
              type="number"
              value={qty}
              onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) setQty(v); }}
              className="flex-1 h-12 text-center text-xl font-bold bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <button onClick={() => setQty(q => q + 1)}
              className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all">
              <Plus className="w-5 h-5 text-white" />
            </button>
          </div>
          <div className="flex gap-2 mt-2">
            {[1, 5, 10, 12, 24].map(n => (
              <button key={n} onClick={() => setQty(n)}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all ${qty === n ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}>
                {n}
              </button>
            ))}
          </div>
        </div>

        {/* Price + Discount */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              سعر الوحدة ({cur()})
              {!allowEditPrice && <span className="ml-1 text-[10px] text-amber-600">🔒 ثابت</span>}
            </p>
            <input
              type="number"
              value={unitPrice}
              onChange={e => {
                if (!allowEditPrice) return;
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setUnitPrice(v);
              }}
              readOnly={!allowEditPrice}
              className={`w-full h-10 px-3 text-sm font-medium border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                allowEditPrice
                  ? 'bg-card border-border'
                  : 'bg-muted/50 border-muted-foreground/20 cursor-not-allowed text-muted-foreground'
              }`}
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">خصم %</p>
            <input
              type="number"
              value={discount}
              onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v >= 0 && v <= 100) setDiscount(v); }}
              className="w-full h-10 px-3 text-sm font-medium bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
              min={0} max={100}
            />
          </div>
        </div>

        {/* Subtotal */}
        <div className="flex justify-between items-center bg-primary/5 rounded-xl p-3">
          <span className="text-sm text-muted-foreground">الإجمالي الفرعي</span>
          <span className="font-bold text-primary text-lg">{subtotal.toLocaleString('ar-EG', { minimumFractionDigits: 2 })} {cur()}</span>
        </div>

        <Button
          onClick={() => onAdd({ product, quantity: qty, price_unit: unitPrice, discount, subtotal })}
          className="w-full bg-primary h-12 text-base font-semibold rounded-xl gap-2"
        >
          <ShoppingCart className="w-5 h-5" />
          {existing ? 'تحديث' : 'إضافة للطلب'}
        </Button>
      </motion.div>
    </motion.div>
  );
}

const TABS = [
  { key: 'all', label: 'الكل' },
  { key: 'bestsellers', label: 'الأكثر مبيعاً' },
];

export default function ProductPicker({ orderLines, priceMap, onUpdateLine, onRemoveLine, onContinue, customerId }) {
  const { profile: repProfile, isManager } = useRepProfile();
  // Managers always edit; reps follow their profile flag.
  const allowEditPrice = isManager || !!repProfile.allow_edit_price;
  const [products, setProducts] = useState([]);
  const [imageMap, setImageMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [activeSheet, setActiveSheet] = useState(null); // product being added
  const searchTimer = useRef(null);
  const imageTimer = useRef(null);

  const cartCount = orderLines.length;
  const cartQty = orderLines.reduce((s, l) => s + l.quantity, 0);
  const cartTotal = orderLines.reduce((s, l) => s + l.quantity * l.price_unit * (1 - (l.discount || 0) / 100), 0);

  const fetchProducts = async (q, bestsellers = false) => {
    setLoading(true);
    // Prefer the SellGo endpoint so reps get only products they're
    // entitled to (warehouse + dealer category). Falls back to the
    // legacy /products if the SellGo addon isn't installed.
    const params = {
      search: q || undefined,
      limit: bestsellers ? 20 : 40,
      order: bestsellers ? 'sales_count desc' : undefined,
    };
    let res;
    try {
      try {
        res = await api.get('/sellgo_inventory', params);
      } catch {
        try {
          res = await api.get('/sellgo-products', params);
        } catch {
          res = await api.get('/products', params);
        }
      }
      if (res?.success) {
        const data = res.data || [];
        setProducts(data);
        // Lazy-load images
        clearTimeout(imageTimer.current);
        imageTimer.current = setTimeout(() => {
          const missing = data.map(p => p.id).filter(id => !(id in imageMap));
          if (missing.length) {
            api.post('/products/images', { product_ids: missing.slice(0, 40) })
              .then(r => { if (r?.success) setImageMap(prev => ({ ...prev, ...r.data })); })
              .catch(() => {});
          }
        }, 300);
      }
    } catch {
      /* offline + uncached → keep whatever products we already have */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts('', tab === 'bestsellers'); }, [tab]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchProducts(val, false), 350);
  };

  const handleTapProduct = (product) => {
    setActiveSheet(product);
  };

  const handleAddLine = ({ product, quantity, price_unit, discount }) => {
    const price = priceMap[product.id] !== undefined ? priceMap[product.id] : price_unit;
    onUpdateLine({
      template_id: product.id,
      product_variant_id: null,
      name: product.name,
      default_code: product.default_code,
      quantity,
      price_unit: price,
      discount: discount || 0,
      uom: product.uom_id ? (Array.isArray(product.uom_id) ? product.uom_id[1] : product.uom_id) : 'وحدة',
      // VAT rate (SellGo's own database only; Odoo lines carry none and show no VAT row)
      tax_rate: product.tax_rate,
    });
    setActiveSheet(null);
  };

  const displayedProducts = products.map(p => ({
    ...p,
    _imgSrc: imageMap[p.id] || null,
  }));

  return (
    <div className="flex flex-col h-full">
      {/* Sticky cart summary */}
      <div className="bg-gradient-to-l from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-3 flex items-center justify-between mb-3 shadow-sm">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
            <ShoppingCart className="w-5 h-5 text-primary" />
          </div>
          <div className="leading-tight">
            <p className="text-sm font-bold text-primary">{cartCount} منتج • {cartQty} قطعة</p>
            <p className="text-[11px] text-primary/70">سلة الطلب</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-bold text-primary">{cartTotal.toLocaleString('ar-EG', { minimumFractionDigits: 2 })} {cur()}</span>
          {cartCount > 0 && (
            <Button onClick={onContinue} size="sm" className="bg-primary h-8 text-xs rounded-xl">
              مراجعة الطلب
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-2">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => handleSearch(e.target.value)}
          placeholder="بحث بالاسم أو الكود..."
          className="pr-10 bg-card rounded-xl"
        />
        {search && (
          <button onClick={() => handleSearch('')} className="absolute left-3 top-1/2 -translate-y-1/2">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-3">
        {TABS.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); if (!search) setSearch(''); }}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
              tab === t.key ? 'bg-primary text-white' : 'bg-card border border-border text-muted-foreground'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Product grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="bg-card border border-border rounded-2xl overflow-hidden animate-pulse">
              <div className="w-full h-24 bg-muted" />
              <div className="p-2.5 space-y-1.5">
                <div className="h-3 bg-muted rounded w-1/3" />
                <div className="h-4 bg-muted rounded w-full" />
                <div className="h-4 bg-muted rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {displayedProducts.map(p => {
            const inCart = orderLines.find(l => l.template_id === p.id);
            const price = priceMap[p.id] !== undefined ? priceMap[p.id] : p.list_price;
            return (
              <ProductCard
                key={p.id}
                product={p}
                price={price}
                inCart={inCart}
                onTap={handleTapProduct}
              />
            );
          })}
        </div>
      )}

      {/* Add product sheet */}
      <AnimatePresence>
        {activeSheet && (
          <AddProductSheet
            product={activeSheet}
            price={priceMap[activeSheet.id] !== undefined ? priceMap[activeSheet.id] : activeSheet.list_price}
            existing={orderLines.find(l => l.template_id === activeSheet.id)}
            onAdd={handleAddLine}
            onClose={() => setActiveSheet(null)}
            allowEditPrice={allowEditPrice}
          />
        )}
      </AnimatePresence>
    </div>
  );
}